import React, { useRef } from 'react'
import { useAsync } from 'react-async'
import { useRecoilValue } from 'recoil'
import styled, { css } from 'styled-components'
import SkeletonBanner from 'components/Skeleton/SkeletonBanner'
import BannerSlider from 'components/TopBanner/TopBannerSlider'
import { STORE_ROOT_PROPS } from 'core/store/global'
import API from 'core/api'
import { getUA } from 'core/singleton'
import { BRIDGE } from 'core/constants/bridge'
import { requestBridge } from 'core/util/default'
import { STORE_MARKETING_GTM } from 'core/store/analysis'
import SectionLayout from 'container/SectionLayout'
import { CUSTOM_CLASS_NAME } from 'core/constants'

interface SectionTopBannerProps {}

const SectionTopBanner: React.FC<SectionTopBannerProps> = (props) => {
  const root = useRecoilValue(STORE_ROOT_PROPS)
  const marketingGTM = useRecoilValue(STORE_MARKETING_GTM)
  const orderType = 'show'
  const { data, isLoading } = useAsync({
    promiseFn: API.getTopBanner,
    orderType: orderType,
    watch: orderType,
  })
  const isBridge = root?.bridge
  const bannerRef = useRef<HTMLDivElement>(null)
  const { isSDK, isSDKIOS } = getUA()

  const handleClickBanner = (item: GetTopBannerItem) => {
    try {
      API.patchTopBannerCount(item.topBannerId)
    } catch (error) {
      console.log(error)
    }

    if (root?.bridge || window[BRIDGE.TOP_BANNER] || isSDK) {
      if (marketingGTM?.checkAvailability()) {
        const queryStringPrefix = item.link.indexOf('?') >= 0 ? '&' : '?'
        item.link = item.link.concat(`${queryStringPrefix}_gl=${marketingGTM.getGl()}`)
      }

      requestBridge(BRIDGE.TOP_BANNER, {
        bannerLinkUrl: item.link,
      })
      return true
    }
    return false
  }
  // disalbeShow = 상시노출 여부
  const getIsShowDate = (disalbeShow: boolean, startTime: string, endTime: string) => {
    if (disalbeShow || !startTime || !endTime) return true
    const today = new Date().toISOString()
    return today > startTime && today < endTime
  }
  const showList = data?.items.filter((item) =>
    getIsShowDate(item.disableShow, item.showStartTime, item.showEndTime),
  )

  return (
    <SectionLayout
      elementId="topBanner"
      className={`${CUSTOM_CLASS_NAME.TOP_BANNER}`}
      trigger={`${isLoading}`}
    >
      <StyledSectionTopBanner ref={bannerRef} isSDKIOS={isSDKIOS}>
        {isLoading ? (
          <SkeletonBanner />
        ) : (
          <BannerSlider list={showList} handleClickBanner={handleClickBanner} isBridge={isBridge} />
        )}
      </StyledSectionTopBanner>
    </SectionLayout>
  )
}

const StyledSectionTopBanner = styled.div<{ isSDKIOS: boolean }>`
  position: relative;
  ${({ isSDKIOS }) =>
    isSDKIOS &&
    css`
      overflow-y: hidden;
    `}
  .slick-dots li button:before {
    content: '';
  }
  .slick-dots,
  .slick-next,
  .slick-prev {
    position: absolute;
    display: block;
    padding: 0;
  }
  .slick-next:before,
  .slick-prev:before {
    font-family: slick;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  .slick-loading .slick-list {
    background: url(ajax-loader.gif) center center no-repeat #fff;
  }
  .slick-next,
  .slick-prev {
    font-size: 0;
    line-height: 0;
    top: 50%;
    width: 20px;
    height: 20px;
    -webkit-transform: translate(0, -50%);
    -ms-transform: translate(0, -50%);
    transform: translate(0, -50%);
    cursor: pointer;
    color: transparent;
    border: none;
    outline: 0;
    background: 0 0;
  }
  .slick-next:focus,
  .slick-next:hover,
  .slick-prev:focus,
  .slick-prev:hover {
    color: transparent;
    outline: 0;
    background: 0 0;
  }
  .slick-next:focus:before,
  .slick-next:hover:before,
  .slick-prev:focus:before,
  .slick-prev:hover:before {
    opacity: 1;
  }
  .slick-next.slick-disabled:before,
  .slick-prev.slick-disabled:before {
    opacity: 0.25;
  }
  .slick-next:before,
  .slick-prev:before {
    font-size: 20px;
    line-height: 1;
    opacity: 0.75;
    color: #fff;
  }
  .slick-prev {
    left: -25px;
  }
  [dir='rtl'] .slick-prev {
    right: -25px;
    left: auto;
  }
  .slick-prev:before {
    content: '←';
  }
  .slick-next:before,
  [dir='rtl'] .slick-prev:before {
    content: '→';
  }
  .slick-next {
    right: -25px;
  }
  [dir='rtl'] .slick-next {
    right: auto;
    left: -25px;
  }
  [dir='rtl'] .slick-next:before {
    content: '←';
  }
  .slick-dotted.slick-slider {
    margin-bottom: 30px;
  }
  .slick-dots {
    bottom: -25px;
    width: 100%;
    margin: 0;
    list-style: none;
    text-align: center;
  }
  .slick-dots li {
    position: relative;
    display: inline-block;
    width: 20px;
    height: 20px;
    margin: 0 5px;
    padding: 0;
    cursor: pointer;
  }
  .slick-list,
  .slick-slider,
  .slick-track {
    position: relative;
    display: block;
  }
  .slick-loading .slick-slide,
  .slick-loading .slick-track {
    visibility: hidden;
  }
  .slick-slider {
    box-sizing: border-box;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    -webkit-touch-callout: none;
    -khtml-user-select: none;
    -ms-touch-action: pan-y;
    touch-action: pan-y;
    -webkit-tap-highlight-color: transparent;
  }
  .slick-list {
    overflow: hidden;
    margin: 0;
    padding: 0;
  }
  // .slick-list:focus {
  //   outline: 0;
  // }
  .slick-list.dragging {
    cursor: pointer;
    cursor: hand;
  }
  .slick-slider .slick-list,
  .slick-slider .slick-track {
    -webkit-transform: translate3d(0, 0, 0);
    -moz-transform: translate3d(0, 0, 0);
    -ms-transform: translate3d(0, 0, 0);
    -o-transform: translate3d(0, 0, 0);
    transform: translate3d(0, 0, 0);
  }
  .slick-track {
    top: 0;
    left: 0;
  }
  .slick-track:after,
  .slick-track:before {
    display: table;
    content: '';
  }
  .slick-track:after {
    clear: both;
  }
  .slick-slide {
    display: none;
    float: left;
    min-height: 1px;
  }
  [dir='rtl'] .slick-slide {
    float: right;
  }
  .slick-slide img {
    display: block;
  }
  .slick-slide.slick-loading img {
    display: none;
  }
  .slick-slide.dragging img {
    pointer-events: none;
  }
  .slick-initialized .slick-slide {
    display: block;
  }
  .slick-vertical .slick-slide {
    display: block;
    height: auto;
    border: 1px solid transparent;
  }
  .slick-arrow.slick-hidden {
    display: none;
  }
`

export default SectionTopBanner
